import { Component, Input, OnInit } from '@angular/core';
import { EnumCardVerificationForm, EnumCardVerificationSection, shippingAddressBodyData } from '../distributor-data/distributor-data.data';
import { BehaviorSubject, of } from 'rxjs';
import {
  IAddressLocationData,
  ICardVerificationForm,
  IDeliveryAddressNote,
  INoteChangeVerification,
  IPayloadRegistrationDeliveryAddress,
  IPayloadRegistrationVerification,
  IShippingAddressStep,
} from '../../../../interfaces/detail-registration.interface';
import { DetailRegistrationDistributorService } from '../detail-registration.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { DistributorStatusEnum, DistributorTabStatus, StatusChangeEnum } from '@config/enum/distributor.enum';
import { EnumVerificationStatus } from '@pages/sales-marketing/program-marketing-legacy/program-marketing.enum';
import { DistributorService } from '../../../../distributor.service';
import { EnumDistributorRevisionSession } from '@config/enum/session.enum';

@Component({
  selector: 'app-shipping-address',
  templateUrl: './shipping-address.component.html',
  styleUrls: ['./shipping-address.component.scss'],
})
export class ShippingAddressComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() isViewOnly = false;

  dataValue: BehaviorSubject<IShippingAddressStep> = new BehaviorSubject<IShippingAddressStep>({} as IShippingAddressStep);
  isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  revisionNoteData: IPayloadRegistrationDeliveryAddress;
  snapshotParams!: Params;
  revisionType: EnumDistributorRevisionSession;

  get isNeedValidation() {
    return this.snapshotParams.status !== DistributorTabStatus.REGISTERED && !this.isViewOnly;
  }

  get DeliveryAddressForm() {
    return this.form.get('delivery_address') as FormArray;
  }

  get ShowBadge() {
    return this.detailRegistrationService.showBadgePendingDetail();
  }

  constructor(
    private fb: FormBuilder,
    public detailRegistrationService: DetailRegistrationDistributorService,
    private activatedRoute: ActivatedRoute,
    private distributorService: DistributorService
  ) {
    this.snapshotParams = this.activatedRoute.snapshot.params;
    if (this.snapshotParams.status === DistributorTabStatus.REGISTERED) {
      this.isViewOnly = true;
    }
    const statusTab = this.activatedRoute.snapshot.params.tab;
    this.revisionType = this.detailRegistrationService.getTypeRevision(statusTab);
  }

  ngOnInit(): void {
    this.getRevisionNoteData();
    this.getData();
  }

  getRevisionNoteData() {
    this.revisionNoteData = this.detailRegistrationService.getSessionRevisionNote(this.revisionType) ?? ({} as IPayloadRegistrationVerification);
  }

  getData() {
    this.detailRegistrationService.getShippingAddressStep().subscribe((value) => {
      if (!value) return;
      this.dataValue.next(value);
      if (this.isNeedValidation) {
        this.generateForm(value);
        this.handleValidationForm();
      }
      this.isLoading.next(false);
    });
  }

  generateForm(value: IShippingAddressStep) {
    const addresses = value.delivery_address ?? [];
    if (!addresses.length) return;

    const controls = this.fb.array(addresses.map((address) => this.createDeliveryAddressGroup(address)));

    this.form.setControl('delivery_address', controls);
    this.revisionNoteData.delivery_address = this.DeliveryAddressForm.value as IDeliveryAddressNote[];
    this.detailRegistrationService.setSessionRevisionNote(this.revisionNoteData, this.revisionType);
  }

  private createDeliveryAddressGroup(address: any) {
    const revisionList = this.revisionNoteData.delivery_address;
    const id = this.getIdShippingAddress(address);

    const foundRevision = revisionList?.find((rev) => rev.id === id);
    const needsNote = address.status_verified_enum === EnumVerificationStatus.REQUEST_REVISION;
    const note = needsNote && foundRevision?.note ? foundRevision.note : '';

    return this.fb.group({
      id: [id],
      note: [note ?? '', Validators.required],
      status_verified_enum: [address.status_verified_enum],
      status_change_enum: [address.status_change_enum],
      revision_data: address.revision_data,
    });
  }

  generateBodyData(item: IAddressLocationData) {
    const _body: ICardVerificationForm[] = shippingAddressBodyData;
    return this.detailRegistrationService.generateBodyData(item, _body);
  }

  noteChanged(e: INoteChangeVerification, id: string) {
    const { delivery_address } = this.revisionNoteData;
    const { note } = e;

    delivery_address?.flatMap((val) => {
      if (val.id === id) {
        val.note = note;
      }
    });
    !this.isViewOnly && this.handleValidationForm();
    this.detailRegistrationService.setSessionRevisionNote(this.revisionNoteData, this.revisionType);
  }

  noteValue(id: string) {
    if (this.isViewOnly) return '';
    const delivery_address = this.revisionNoteData.delivery_address ?? [];
    if (!!delivery_address && delivery_address.length > 0) {
      const _search = delivery_address.find((value) => value.id === id);
      this.handleValidationForm();
      return _search ? _search.note : '';
    }
  }

  handleValidationForm() {
    if (!this.isNeedValidation) return;
    const { delivery_address } = this.revisionNoteData;

    delivery_address &&
      delivery_address.map((value) => {
        const { id, note, status_verified_enum, status_change_enum } = value;
        const control = this.DeliveryAddressForm.controls.find((control) => control.get('id')?.value === id);
        const noteControl = control?.get('note');
        const _findNote = this.revisionNoteData.delivery_address?.find((value) => value.id === id);
        if (control && noteControl) {
          const statusHeader = this.distributorService.DistributorDetailHeader.status_enum;
          const _checkValidator =
            status_verified_enum !== EnumVerificationStatus.REQUEST_REVISION &&
            statusHeader !== DistributorStatusEnum.PENDING_REGISTRATION &&
            status_change_enum !== StatusChangeEnum.ADD;
          const _checkRequest = (status_verified_enum === EnumVerificationStatus.REQUEST_REVISION && !!note) || status_verified_enum === EnumVerificationStatus.VERIFIED;

          if (status_verified_enum && status_change_enum !== StatusChangeEnum.DELETE) {
            if (_checkValidator) {
              noteControl.clearValidators();
            } else if (_checkRequest && this.detailRegistrationService.checkValidationFormStepper(control, value) && this.detailRegistrationService.isVerifiedAndRevision(value)) {
              noteControl.clearValidators();
            } else {
              noteControl.setValidators([Validators.required]);
              noteControl.setValue(_findNote && _findNote.note);
            }
          } else if (status_change_enum === StatusChangeEnum.DELETE || status_change_enum === StatusChangeEnum.NOT_CHANGE) {
            noteControl.clearValidators();
          }
          noteControl.updateValueAndValidity();
        }
      });
  }

  async afterActionVerification(event: string | null) {
    if (event === null) {
      this.getRevisionNoteData();
    }
    setTimeout(() => {
      this.getData();
    }, 500);
  }

  getIdShippingAddress = (item: IAddressLocationData) => (item.revision_data ? item.revision_data.id : item.current_data.id);

  protected readonly EnumCardVerificationForm = EnumCardVerificationForm;
  protected readonly EnumCardVerificationSection = EnumCardVerificationSection;
  protected readonly of = of;
}
